import { Add, TrashCan } from "@carbon/icons-react";
import { Button, Heading, Section, Stack } from "@carbon/react";
import { useFieldArray, UseFormReturn } from "react-hook-form";

import { useT } from "src/internationalization";

import { CreateSimpleRuleIntroduction } from "./CreateSimpleRuleIntroduction";
import { SimpleRuleOperatorsFormField } from "./SimpleRuleOperatorsFormField";
import { SimpleRulePropertyFormField } from "./SimpleRulePropertyFormField";
import { SimpleRuleValueFormField } from "./SimpleRuleValueFormField";
import { SimleRuleFormData } from "./useSimpleRuleDefinition";

export type SimpleRuleDefinitionProps = {
  caseTypeCode?: string;
  methods: UseFormReturn<SimleRuleFormData>;
};

export const SimpleRuleDefinition: React.FC<SimpleRuleDefinitionProps> = ({
  caseTypeCode,
  methods,
}) => {
  const t = useT();

  const control = methods.control;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "criteria",
  });

  const handleAddCriterion = () => {
    append({ property: null, operator: null, value: "" });
  };

  const handleDeleteCriterion = (index: number) => {
    // Prevent deletion if there's only one criterion
    if (fields.length === 1) return;
    remove(index);
  };

  return (
    <>
      <Stack gap={2}>
        <div className="flex flex-col gap-6">
          <Section level={3}>
            <Heading className={"ml-0"}>{t("page.rules.simple.modal.create.title")}</Heading>
          </Section>
          <CreateSimpleRuleIntroduction />
        </div>
        <div>
          {/* Table Header */}
          <div className="grid grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_48px] p-2 bg-layer01">
            <div className="text-sm font-semibold">{t("cases.table.property.column.title")}</div>
            <div className="text-sm font-semibold">{t("page.rules.simple.example.operator")}</div>
            <div className="text-sm font-semibold">{t("page.rules.simple.example.value")}</div>
            <div></div>
          </div>
          {fields.map((field, index) => {
            return (
              <div
                key={field.id}
                className="grid grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_48px] gap-4 p-2 items-start"
              >
                <SimpleRulePropertyFormField
                  index={index}
                  id={field.id}
                  caseTypeCode={caseTypeCode}
                  methods={methods}
                />
                <SimpleRuleOperatorsFormField id={field.id} index={index} methods={methods} />
                <SimpleRuleValueFormField index={index} id={field.id} methods={methods} />

                <div className="flex items-center justify-center pt-2">
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={TrashCan}
                    iconDescription="Supprimer"
                    onClick={() => handleDeleteCriterion(index)}
                    disabled={fields.length === 1}
                    className="hover-danger"
                  />
                </div>
              </div>
            );
          })}

          <div className="pt-2">
            <Button kind="ghost" size="sm" renderIcon={Add} onClick={handleAddCriterion}>
              {t("page.rules.modal.create.form.add.criteria")}
            </Button>
          </div>
        </div>
      </Stack>
    </>
  );
};
